import { Component } from "@theme/component";
import { ThemeEvents, MediaStartedPlayingEvent } from "@theme/events";
import { DialogCloseEvent } from "@theme/dialog";

const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The play/pause button rendered by `video.liquid` (`custom_controls`)
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /**
   * Last known playback state. For uploaded `<video>` it mirrors the element's own play/pause
   * events; for iframes and models (which emit nothing we can read) it is what we last asked for.
   * @type {boolean}
   */
  isPlaying = false;

  /**
   * What the visitor last asked for via the toggle, if anything. Programmatic play/pause from the
   * gallery (slide enter/leave) doesn't touch it, so a visitor's pause survives swiping away and
   * back. Lives on the element, so it resets whenever the gallery is re-rendered.
   * @type {"play" | "pause" | null}
   */
  #userIntent = null;

  #abortController = new AbortController();

  /** @type {AbortController | null} */
  #videoListeners = null;

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    // Only a dialog that holds this media should stop it on close. Horizon paused every
    // deferred-media on any dialog close, which stopped the autoplaying PDP gallery video every
    // time the cart drawer was dismissed.
    window.addEventListener(
      DialogCloseEvent.eventName,
      (event) => {
        if (event.target instanceof Node && event.target.contains(this)) this.pauseMedia();
      },
      { signal }
    );

    // Autoplay is suppressed under reduced motion (see #shouldAutoplay), so tell the toggle
    // straight away instead of letting it promise a playback that won't come.
    if (this.hasAttribute("autoplay") && reducedMotion.matches) {
      this.#setState(false);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#videoListeners?.abort();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    if (this.hasAttribute("data-state")) {
      this.dataset.state = isPlaying ? "playing" : "paused";
    }

    const { toggleMediaButton } = this.refs;
    if (!(toggleMediaButton instanceof HTMLElement)) return;

    const label = isPlaying ? toggleMediaButton.dataset.pauseLabel : toggleMediaButton.dataset.playLabel;
    if (label) toggleMediaButton.setAttribute("aria-label", label);
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.#setState(true);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute("data-media-loaded")) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector("template")?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    // Strip the attribute before the element enters the document, or the browser autoplays it
    // regardless of what we decide below.
    const autoplayRequested = content instanceof HTMLVideoElement && content.hasAttribute("autoplay");
    if (autoplayRequested && !this.#shouldAutoplay()) {
      content.removeAttribute("autoplay");
    }

    this.setAttribute("data-media-loaded", "true");
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add("deferred-media__playing");

    if (content instanceof HTMLVideoElement) {
      this.#bindVideo(content);

      if (autoplayRequested) {
        // The gallery may mount this on a slide that's being swiped to, so rely on the same
        // muted-inline contract the markup declares rather than on the browser having read it.
        content.muted = true;
        content.playsInline = true;

        if (this.#shouldAutoplay()) {
          this.#play(content);
        } else {
          this.#setState(false);
        }
      }
    }
  }

  /**
   * Toggle play/pause state of the media. This is the visitor's button, so it records intent.
   */
  toggleMedia() {
    const video = this.querySelector("video");
    const playing = video ? !video.paused : this.isPlaying;

    if (playing) {
      this.pauseMedia({ userInitiated: true });
    } else {
      this.playMedia({ userInitiated: true });
    }
  }

  /**
   * Plays the media. Programmatic calls (from the gallery on slide enter) defer to the visitor: a
   * remembered pause wins, and with no expressed intent reduced motion means "leave it paused".
   * @param {{ userInitiated?: boolean } | Event} [options]
   */
  playMedia(options) {
    const userInitiated = this.#isUserInitiated(options);

    if (userInitiated) {
      this.#userIntent = "play";
    } else if (this.#userIntent === "pause" || (this.#userIntent === null && !this.#shouldAutoplay())) {
      return;
    }

    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector("iframe[data-video-type]");
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === "youtube"
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        "*"
      );
      this.#setState(true);
      return;
    }

    const video = this.querySelector("video");
    if (video) {
      this.#play(video);
    } else {
      this.#setState(true);
    }
  }

  /**
   * Pauses the media
   * @param {{ userInitiated?: boolean } | Event} [options] - Also receives the Event when bound as a listener.
   */
  pauseMedia(options) {
    if (this.#isUserInitiated(options)) {
      this.#userIntent = "pause";
    }

    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector("iframe[data-video-type]");

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === "youtube"
          ? '{"event":"command","func":"' + "pauseVideo" + '","args":""}'
          : '{"method":"pause"}',
        "*"
      );
      this.isPlaying = false;
      this.updatePlayPauseHint(false);
      return;
    }

    const video = this.querySelector("video");
    if (video) {
      // The `pause` event (see #bindVideo) updates the state.
      video.pause();
      return;
    }

    // Nothing mounted yet (the gallery hasn't shown this slide): only the flag can change.
    this.isPlaying = false;
  }

  /**
   * @param {unknown} options
   * @returns {boolean}
   */
  #isUserInitiated(options) {
    return typeof options === "object" && options !== null && !(options instanceof Event) && "userInitiated" in options
      ? options.userInitiated === true
      : false;
  }

  /**
   * Whether an autoplaying video should actually start on its own right now.
   * @returns {boolean}
   */
  #shouldAutoplay() {
    return !reducedMotion.matches;
  }

  /**
   * Starts a mounted video, reporting a refusal (Low Power Mode, data saver, policy) as "paused"
   * so the toggle offers play instead of pretending.
   * @param {HTMLVideoElement} video
   */
  #play(video) {
    const attempt = video.play();
    if (attempt && typeof attempt.catch === "function") {
      attempt.catch(() => this.#setState(false));
    }
  }

  /**
   * Mirrors the `<video>`'s real playback state into the toggle. `play` fires when playback is
   * requested (autoplay included), `pause` when it stops for any reason; together they cover the
   * gallery's programmatic pauses and the visitor's own taps alike.
   * @param {HTMLVideoElement} video
   */
  #bindVideo(video) {
    this.#videoListeners?.abort();
    this.#videoListeners = new AbortController();
    const { signal } = this.#videoListeners;

    video.addEventListener("play", () => this.#setState(true), { signal });
    video.addEventListener("pause", () => this.#setState(false), { signal });
  }

  /**
   * @param {boolean} isPlaying
   */
  #setState(isPlaying) {
    this.isPlaying = isPlaying;
    this.updatePlayPauseHint(isPlaying);
  }
}

if (!customElements.get("deferred-media")) {
  customElements.define("deferred-media", DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: "model-viewer-ui",
        version: "1.0",
        onLoad: this.setupModelViewerUI.bind(this)
      }
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia({ userInitiated: true });
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector("model-viewer");
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      "pointerdown",
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      "click",
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get("product-model")) {
  customElements.define("product-model", ProductModel);
}
